// src/services/queries/macro/fedRateProbabilitiesAdapter.ts

import type { FedRateProbabilitiesResponse, FedRateTargetRange } from '@/types/dashboards/macro';

export interface TransformedFedRateProbability {
	rateRange: string;
	probability: number;
	oneDayAgo: number;
	oneWeekAgo: number;
	oneMonthAgo: number;
	isCurrentRate: boolean;
}

export interface TransformedFedData {
	currentRateRange: string;
	calculatedProbabilities: TransformedFedRateProbability[];
	totalCutsProbability: number;
	noChangeProbability: number;
	rateHikesProbability: number;
	currentFedRateUpper: number | null;
}

export interface MeetingDateData {
	meetingDate: string;
	probabilities: TransformedFedRateProbability[];
	totalCutsProbability: number;
	noChangeProbability: number;
	rateHikesProbability: number;
}

export interface TransformedFedDataWithMeetings {
	currentRateRange: string;
	currentFedRateUpper: number | null;
	meetingDates: MeetingDateData[];
}

/**
 * Transform API response data to the format expected by the component
 */
export const transformFedRateProbabilities = (
	apiData: FedRateProbabilitiesResponse
): TransformedFedDataWithMeetings => {
	// Transform each meeting date group
	const meetingDates: MeetingDateData[] = [];
	let currentRateRange = '';

	apiData.data.forEach((meetingItem) => {
		// Handle the target_ranges array structure
		const targetRanges = meetingItem.target_ranges || [];
		
		const transformedProbabilities = targetRanges.map((item: FedRateTargetRange) => {
			const isCurrentRate = item.is_current;

			return {
				rateRange: item.target_range || '',
				probability: (item.probability_current || 0), // Already in percentage format
				oneDayAgo: (item.probability_1d || 0), // Already in percentage format
				oneWeekAgo: (item.probability_1w || 0), // Already in percentage format
				oneMonthAgo: (item.probability_1m || 0), // Already in percentage format
				isCurrentRate,
			};
		});

		// Sort by target range (numerically)
		transformedProbabilities.sort((a: TransformedFedRateProbability, b: TransformedFedRateProbability) => {
			const aMin = parseInt(a.rateRange.split('-')[0] || '0');
			const bMin = parseInt(b.rateRange.split('-')[0] || '0');
			return aMin - bMin;
		});

		// Find the current rate range (current target bucket)
		const currentRange = transformedProbabilities.find((item: TransformedFedRateProbability) => item.isCurrentRate);
		if (currentRange && !currentRateRange) {
			currentRateRange = currentRange.rateRange;
		}

		// Calculate summary probabilities
		const currentRangeIndex = transformedProbabilities.findIndex((item: TransformedFedRateProbability) => item.isCurrentRate);
		const noChangeProbability = currentRangeIndex >= 0 && transformedProbabilities[currentRangeIndex] 
			? transformedProbabilities[currentRangeIndex].probability 
			: 0;

		// Calculate rate cuts (lower target ranges - smaller numbers)
		const totalCutsProbability = transformedProbabilities
			.filter((_: TransformedFedRateProbability, index: number) => index < currentRangeIndex)
			.reduce((sum: number, item: TransformedFedRateProbability) => sum + item.probability, 0);

		// Calculate rate hikes (higher target ranges - larger numbers)
		const rateHikesProbability = transformedProbabilities
			.filter((_: TransformedFedRateProbability, index: number) => index > currentRangeIndex)
			.reduce((sum: number, item: TransformedFedRateProbability) => sum + item.probability, 0);

		meetingDates.push({
			meetingDate: meetingItem.meeting_date,
			probabilities: transformedProbabilities,
			totalCutsProbability,
			noChangeProbability,
			rateHikesProbability,
		});
	});

	// Sort meeting dates chronologically
	meetingDates.sort((a, b) => new Date(a.meetingDate).getTime() - new Date(b.meetingDate).getTime());

	return {
		currentRateRange,
		currentFedRateUpper: apiData.current_fed_rate_upper,
		meetingDates,
	};
};
